import * as Phaser from 'phaser';
import { SCENES } from '@/types';
import { GAME_HEIGHT, GAME_WIDTH } from '@/config/gameConfig';
import { TEXTURE_KEYS } from '@/utils/textures';
import { isTutorialSeen, markTutorialSeen } from '@/utils/storage';

const COLORS = {
  primary: 0x3b9f4a,
  primaryHover: 0x2f8a3d,
  text: '#ffffff',
  subtext: '#fff8e1',
  card: 0xffffff,
} as const;

export class IntroScene extends Phaser.Scene {
  private tutorialLayer?: Phaser.GameObjects.Container;
  private elephantBob?: Phaser.Tweens.Tween;

  constructor() {
    super(SCENES.Intro);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#87ceeb');

    this.drawBackdrop();
    this.drawTitle();
    this.drawElephantPreview();
    this.drawPlayButton();
    this.drawTutorialLink();

    if (!isTutorialSeen()) {
      this.showTutorial();
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.elephantBob?.stop();
    });
  }

  private drawBackdrop(): void {
    const ground = this.add.graphics();
    ground.fillStyle(0x66bb6a, 1);
    ground.fillRect(0, GAME_HEIGHT - 80, GAME_WIDTH, 80);
    ground.fillStyle(0x4caf50, 1);
    ground.fillRect(0, GAME_HEIGHT - 80, GAME_WIDTH, 4);
  }

  private drawTitle(): void {
    this.add
      .text(GAME_WIDTH / 2, 60, 'Voi Lớn Lên', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: COLORS.text,
      })
      .setOrigin(0.5)
      .setShadow(0, 3, '#0006', 6, true, true);

    this.add
      .text(GAME_WIDTH / 2, 92, 'Dielac • 60 giây thử thách', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '13px',
        color: COLORS.subtext,
      })
      .setOrigin(0.5);
  }

  private drawElephantPreview(): void {
    const voi = this.add.image(GAME_WIDTH / 2, 250, TEXTURE_KEYS.elephant);
    this.elephantBob = this.tweens.add({
      targets: voi,
      y: 244,
      duration: 700,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private drawPlayButton(): void {
    const btnY = GAME_HEIGHT - 130;
    const btnW = 220;
    const btnH = 60;
    const x = GAME_WIDTH / 2;

    const btn = this.add.graphics();
    btn.fillStyle(COLORS.primary, 1);
    btn.fillRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);
    btn.lineStyle(3, 0xffffff, 1);
    btn.strokeRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);

    const label = this.add
      .text(x, btnY, 'CHƠI NGAY', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: COLORS.text,
      })
      .setOrigin(0.5);

    const hit = this.add
      .zone(x, btnY, btnW, btnH)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerover', () => {
      btn.clear();
      btn.fillStyle(COLORS.primaryHover, 1);
      btn.fillRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);
      btn.lineStyle(3, 0xffffff, 1);
      btn.strokeRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);
    });
    hit.on('pointerout', () => {
      btn.clear();
      btn.fillStyle(COLORS.primary, 1);
      btn.fillRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);
      btn.lineStyle(3, 0xffffff, 1);
      btn.strokeRoundedRect(x - btnW / 2, btnY - btnH / 2, btnW, btnH, 30);
    });
    hit.on('pointerdown', () => {
      this.tweens.add({ targets: [btn, label], scale: 0.95, duration: 80, yoyo: true });
    });
    hit.on('pointerup', () => {
      this.startGame();
    });
  }

  private drawTutorialLink(): void {
    const link = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 60, 'Hướng dẫn', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '14px',
        color: COLORS.text,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const underline = this.add.graphics();
    underline.lineStyle(1, 0xffffff, 0.8);
    const b = link.getBounds();
    underline.lineBetween(b.left, b.bottom + 2, b.right, b.bottom + 2);

    link.on('pointerup', () => this.showTutorial());
  }

  private startGame(): void {
    markTutorialSeen();
    this.scene.start(SCENES.Game);
  }

  private showTutorial(): void {
    if (this.tutorialLayer) return;

    const layer = this.add.container(0, 0).setDepth(100);
    this.tutorialLayer = layer;

    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.55);
    overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    layer.add(overlay);

    const cardX = GAME_WIDTH / 2;
    const cardY = GAME_HEIGHT / 2;
    const cardW = 300;
    const cardH = 420;

    const card = this.add.graphics();
    card.fillStyle(COLORS.card, 1);
    card.fillRoundedRect(cardX - cardW / 2, cardY - cardH / 2, cardW, cardH, 20);
    layer.add(card);

    const title = this.add
      .text(cardX, cardY - cardH / 2 + 30, 'Cách chơi', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#1f1f1f',
      })
      .setOrigin(0.5);
    layer.add(title);

    const instruction = this.add
      .text(
        cardX,
        cardY - cardH / 2 + 70,
        'Vuốt trái / phải để đổi lane.\nDesktop: phím mũi tên ← →.\nNhặt dinh dưỡng, né cua đỏ.',
        {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '14px',
          color: '#444444',
          align: 'center',
          lineSpacing: 4,
        },
      )
      .setOrigin(0.5);
    layer.add(instruction);

    const swipeY = cardY - 70;
    const swipeIndicator = this.add
      .text(cardX, swipeY, '←  vuốt  →', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#3b9f4a',
      })
      .setOrigin(0.5);
    layer.add(swipeIndicator);
    this.tweens.add({
      targets: swipeIndicator,
      x: { from: cardX - 20, to: cardX + 20 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const legendY = cardY + 10;
    const legend: Array<{
      texture: string;
      label: string;
      sub: string;
      color: string;
    }> = [
      { texture: TEXTURE_KEYS.nutrient('hmo'), label: '+', sub: 'Lớn lên', color: '#3b9f4a' },
      { texture: TEXTURE_KEYS.monster, label: '−', sub: 'Nhỏ lại', color: '#d83232' },
      { texture: TEXTURE_KEYS.shield, label: '★', sub: 'Bảo vệ 5s', color: '#e8a020' },
    ];
    const colW = cardW / 3;
    legend.forEach((item, i) => {
      const x = cardX - cardW / 2 + colW * (i + 0.5);
      const icon = this.add.image(x, legendY, item.texture).setScale(0.9);
      layer.add(icon);
      const sign = this.add
        .text(x, legendY + 36, item.label, {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '20px',
          fontStyle: 'bold',
          color: item.color,
        })
        .setOrigin(0.5);
      layer.add(sign);
      const sub = this.add
        .text(x, legendY + 60, item.sub, {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '12px',
          color: '#333333',
        })
        .setOrigin(0.5);
      layer.add(sub);
    });

    const dismissY = cardY + cardH / 2 - 40;
    const dismissW = 180;
    const dismissH = 44;
    const dismissBg = this.add.graphics();
    dismissBg.fillStyle(COLORS.primary, 1);
    dismissBg.fillRoundedRect(
      cardX - dismissW / 2,
      dismissY - dismissH / 2,
      dismissW,
      dismissH,
      22,
    );
    layer.add(dismissBg);
    const dismissLabel = this.add
      .text(cardX, dismissY, 'Đã hiểu', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    layer.add(dismissLabel);
    const dismissHit = this.add
      .zone(cardX, dismissY, dismissW, dismissH)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    layer.add(dismissHit);
    dismissHit.on('pointerup', () => this.hideTutorial());
  }

  private hideTutorial(): void {
    if (!this.tutorialLayer) return;
    this.tweens.add({
      targets: this.tutorialLayer,
      alpha: 0,
      duration: 180,
      onComplete: () => {
        this.tutorialLayer?.destroy();
        this.tutorialLayer = undefined;
      },
    });
  }
}
